"use client";

import { useCallback, useState } from "react";
import { clearPendingRecoveryMethod, stageRecoveryMethod } from "@/lib/auth/last-sign-in-method";
import { resolvePostEmailOtpRedirect } from "@/lib/auth/login-flow";
import { recoveryErrorMessage } from "@/lib/auth/recovery-error-message";
import { authClient } from "@/lib/auth-client";
import { logger } from "@/lib/logger";

/** Custom hook encapsulating email OTP sign-in state and handlers */
export function useEmailOtpSignIn(hasLocalBackup: boolean, setError: (e: string) => void, restoreCallbackURL: string, onAuthenticated?: () => Promise<void>, onAuthenticationStart?: () => AbortSignal) {
  const [showEmailSignIn, setShowEmailSignIn] = useState(false);
  const [emailForOtp, setEmailForOtp] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [emailAuthPending, setEmailAuthPending] = useState(false);
  const handleSendEmailOtp = useCallback(async () => {
    if (!emailForOtp.trim() || emailAuthPending) {
      return;
    }
    setEmailAuthPending(true);
    setError("");
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: emailForOtp.trim(),
        type: "sign-in"
      });
      if (result.error) {
        throw new Error("OTP delivery failed");
      }
      setOtpSent(true);
      logger.info({
        operation: "send_email_otp"
      }, "Email OTP sent for sign-in");
    } catch {
      logger.error({
        operation: "send_email_otp",
        errorType: "OtpDeliveryError"
      }, "Failed to send email OTP");
      setError("Failed to send verification code. Please try again.");
    } finally {
      setEmailAuthPending(false);
    }
  }, [emailForOtp, emailAuthPending, setError]);
  const reportRecoveryError = useCallback((error: unknown) => {
    setError(recoveryErrorMessage(error));
  }, [setError]);
  const handleEmailOtpSignIn = useCallback(async () => {
    const trimmedEmail = emailForOtp.trim();
    const trimmedOtp = otpCode.trim();
    if (!(trimmedEmail && trimmedOtp) || emailAuthPending) {
      return;
    }
    setEmailAuthPending(true);
    setError("");
    try {
      const attemptSignal = onAuthenticationStart?.();
      const result_0 = await authClient.signIn.emailOtp({
        email: trimmedEmail,
        otp: trimmedOtp
      });
      if (result_0.error) {
        throw new Error("OTP verification failed");
      }
      if (attemptSignal?.aborted) {
        return;
      }
      stageRecoveryMethod("email");
      logger.info({
        operation: "email_otp_sign_in"
      }, "Email OTP sign-in successful");
      if (onAuthenticated) {
        await onAuthenticated().catch(reportRecoveryError);
        return;
      }
      if (hasLocalBackup) {
        setShowEmailSignIn(false);
        setOtpSent(false);
        setOtpCode("");
      } else {
        // No local backup on this device. Match the social sign-in flow:
        // existing users go through /restore (which fetches their cloud
        // backup), brand-new users go to /onboarding. We use a hard
        // navigation so the destination's session hooks see the freshly
        // minted Better Auth cookie without any client-cache races.
        const target = await resolvePostEmailOtpRedirect(restoreCallbackURL);
        window.location.href = target;
        return;
      }
    } catch {
      logger.error({
        operation: "email_otp_sign_in",
        errorType: "OtpSignInError"
      }, "Email OTP sign-in failed");
      setError("Invalid or expired verification code. Please try again.");
    } finally {
      setEmailAuthPending(false);
    }
  }, [emailForOtp, otpCode, emailAuthPending, hasLocalBackup, restoreCallbackURL, onAuthenticated, onAuthenticationStart, reportRecoveryError, setError]);
  const resetEmailOtp = useCallback(() => {
    clearPendingRecoveryMethod();
    setShowEmailSignIn(false);
    setOtpSent(false);
    setEmailForOtp("");
    setOtpCode("");
  }, []);
  return {
    showEmailSignIn,
    setShowEmailSignIn,
    emailForOtp,
    setEmailForOtp,
    otpCode,
    setOtpCode,
    otpSent,
    setOtpSent,
    emailAuthPending,
    handleSendEmailOtp,
    handleEmailOtpSignIn,
    resetEmailOtp
  };
}